// Copyright 2025, University of Maryland, All Rights Reserved

/**
 * Hook to fetch workspace config + agents: agents (with slots), slots gating.
 */

import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import type { NodeApiClient } from '@/app-shell/services'
import { useAgents } from '@/subsystems/agents'
import type { Agent } from '@/subsystems/agents'

export interface AppConfig {
  agents: Agent[]
  slotsEnabled: boolean
  isLoading: boolean
}

interface WorkspaceConfigResponse {
  slotsEnabled?: boolean | null
}

export function useAppConfig(
  apiClient: NodeApiClient,
  orgId: string,
  wsId: string,
): AppConfig {
  const { data: wsData, isPending: wsPending } = useQuery({
    queryKey: ['workspaceConfig', orgId, wsId],
    queryFn: async () => {
      const res = await apiClient.get<WorkspaceConfigResponse>(`/workspaces/${orgId}/${wsId}`)
      return res.data
    },
  })

  const { data: agents, isPending: agentsPending } = useAgents(apiClient, orgId, wsId)

  return useMemo(() => ({
    agents: agents ?? [],
    slotsEnabled: wsData?.slotsEnabled ?? false,
    isLoading: wsPending || agentsPending,
  }), [agents, wsData, wsPending, agentsPending])
}
